import { useMemo } from 'react'
import { Sparkles, Star, StarOff } from 'lucide-react'
import {
  CRITERIA_LABELS,
  SCORING_FIELD_KEYS,
  type MarketPersona,
  type MarketSegmentWithScore,
  type ScoringFieldKey,
  type SegmentPersonaLink,
} from '@ispot/shared'
import { initialsFor } from '../segments/segmentCardUtils'

export interface HeatmapRow extends MarketSegmentWithScore {
  liveScore: number
}

interface Props {
  rows: HeatmapRow[]
  beachheadId: string | null
  visibleCriteria: Set<ScoringFieldKey>
  scoringId: string | null
  personas?: MarketPersona[]
  coverage?: SegmentPersonaLink[]
  onToggleBeachhead: (seg: HeatmapRow) => void
  onScoreWithAI?: (seg: HeatmapRow) => void
  onOpenSegment?: (seg: HeatmapRow) => void
}

const CRITERIA_SHORT: Record<ScoringFieldKey, string> = {
  strategicFit: 'Fit',
  marketSize: 'Size',
  marketGrowth: 'Growth',
  easyToServe: 'Serve',
  easyToCompete: 'Compete',
  profitPotential: 'Profit',
}

export function HeatmapTable({
  rows,
  beachheadId,
  visibleCriteria,
  scoringId,
  personas = [],
  coverage = [],
  onToggleBeachhead,
  onScoreWithAI,
  onOpenSegment,
}: Props) {
  const criteria = SCORING_FIELD_KEYS.filter((k) => visibleCriteria.has(k))

  const personaById = useMemo(() => new Map(personas.map((p) => [p.id, p])), [personas])
  const linksBySegment = useMemo(() => {
    const map = new Map<string, MarketPersona[]>()
    for (const link of coverage) {
      const persona = personaById.get(link.personaId)
      if (!persona) continue
      const bucket = map.get(link.segmentId) ?? []
      bucket.push(persona)
      map.set(link.segmentId, bucket)
    }
    return map
  }, [coverage, personaById])
  return (
    <div
      className="overflow-hidden rounded-lg border"
      style={{ borderColor: 'var(--color-border-subtle)' }}
    >
      <table
        className="w-full text-sm"
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr style={{ backgroundColor: 'var(--color-warm-100)' }}>
            <th
              className="sticky left-0 w-[260px] px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider"
              style={{
                color: 'var(--color-text-muted)',
                backgroundColor: 'var(--color-warm-100)',
              }}
            >
              Segment
            </th>
            {criteria.map((k) => (
              <th
                key={k}
                className="w-[80px] px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: 'var(--color-text-muted)' }}
                title={CRITERIA_LABELS[k].description}
              >
                {CRITERIA_SHORT[k]}
              </th>
            ))}
            <th
              className="w-[72px] px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Weighted
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isBeachhead = row.id === beachheadId
            const rowPersonas = linksBySegment.get(row.id) ?? []
            return (
              <tr
                key={row.id}
                onClick={() => onOpenSegment?.(row)}
                className={`group border-t transition-colors hover:bg-[var(--color-warm-50)] ${onOpenSegment ? 'cursor-pointer' : ''}`}
                style={{ borderColor: 'var(--color-border-subtle)' }}
              >
                <td
                  className="px-4 py-3"
                  style={{
                    borderLeft: isBeachhead
                      ? '3px solid var(--color-business, #0984e3)'
                      : '3px solid transparent',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleBeachhead(row)
                      }}
                      className="rounded p-0.5 transition-colors hover:bg-[var(--color-warm-200)]"
                      title={isBeachhead ? 'Clear beachhead' : 'Mark as beachhead'}
                      aria-label={isBeachhead ? 'Clear beachhead' : 'Mark as beachhead'}
                    >
                      {isBeachhead ? (
                        <Star
                          className="h-3.5 w-3.5"
                          style={{
                            color: 'var(--color-business, #0984e3)',
                            fill: 'var(--color-business, #0984e3)',
                          }}
                        />
                      ) : (
                        <StarOff
                          className="h-3.5 w-3.5"
                          style={{ color: 'var(--color-text-muted)' }}
                        />
                      )}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span
                          className="truncate text-sm font-medium"
                          style={{ color: 'var(--color-text)' }}
                        >
                          {row.name}
                        </span>
                        <span
                          className="truncate text-[10px]"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          {row.sector}
                        </span>
                      </div>
                      {rowPersonas.length > 0 ? (
                        <PersonaInlineStack personas={rowPersonas} />
                      ) : null}
                    </div>
                    {onScoreWithAI ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          onScoreWithAI(row)
                        }}
                        disabled={scoringId === row.id}
                        className="ml-auto shrink-0 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-[var(--color-warm-100)] disabled:opacity-100"
                        style={{ color: 'var(--color-market)' }}
                        title={scoringId === row.id ? 'Scoring…' : 'Score this segment with AI'}
                        aria-label="Score with AI"
                      >
                        <Sparkles
                          className={`h-3 w-3 ${scoringId === row.id ? 'animate-pulse' : ''}`}
                        />
                      </button>
                    ) : null}
                  </div>
                </td>
                {criteria.map((k) => {
                  const v = row.scoring[k]?.value ?? null
                  return (
                    <td key={k} className="p-1 text-center align-middle">
                      <ScoreCell value={v} />
                    </td>
                  )
                })}
                <td className="px-2 py-2 text-right font-display tabular-nums">
                  <WeightedBadge value={row.liveScore} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ScoreCell({ value }: { value: number | null }) {
  if (value == null) {
    return (
      <span
        className="inline-flex h-7 w-12 items-center justify-center rounded text-[11px]"
        style={{ color: 'var(--color-warm-400)', backgroundColor: 'var(--color-warm-100)' }}
      >
        —
      </span>
    )
  }
  const t = Math.max(0, Math.min(1, value / 10))
  const bg = `color-mix(in oklab, var(--color-warm-100) ${(1 - t) * 100}%, var(--color-market) ${t * 100}%)`
  const fg = t > 0.55 ? 'white' : 'var(--color-text)'
  return (
    <span
      className="inline-flex h-7 w-12 items-center justify-center rounded text-[12px] font-semibold tabular-nums"
      style={{ backgroundColor: bg, color: fg }}
    >
      {value.toFixed(1)}
    </span>
  )
}

function PersonaInlineStack({ personas }: { personas: MarketPersona[] }) {
  const visible = personas.slice(0, 4)
  const overflow = Math.max(0, personas.length - visible.length)
  return (
    <div className="mt-1 flex items-center gap-1.5">
      <div className="flex items-center">
        {visible.map((p, i) => (
          <span
            key={p.id}
            title={p.name}
            className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 text-[8px] font-bold tracking-[-0.02em]"
            style={{
              marginLeft: i === 0 ? 0 : -5,
              backgroundColor:
                p.role === 'user'
                  ? 'var(--color-market-100, #c9f0f2)'
                  : 'var(--color-business-100, #d6eaf9)',
              color:
                p.role === 'user'
                  ? 'var(--color-market-700, #15747a)'
                  : 'var(--color-business-600, #1e75b8)',
              borderColor: 'var(--color-bg, white)',
            }}
          >
            {initialsFor(p.name)}
          </span>
        ))}
      </div>
      <span className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
        {personas.length} {personas.length === 1 ? 'persona' : 'personas'}
        {overflow > 0 ? ` · +${overflow}` : ''}
      </span>
    </div>
  )
}

function WeightedBadge({ value }: { value: number }) {
  if (value === 0) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>
  }
  const color = value >= 7 ? 'var(--color-market)' : value >= 4 ? '#a17a1f' : '#b14132'
  return <span style={{ color, fontSize: '0.95rem', fontWeight: 700 }}>{value.toFixed(1)}</span>
}
